import { z } from 'zod';

/**
 * Paginação no formato que a API padronizou: `{ itens, total, pagina, tamanho }`.
 *
 * `total` é o do FILTRO, não o da página — é dele que sai a barra de páginas.
 * Toda listagem do painel é paginada no servidor: a tabela de movimentações
 * passa de 5.000 linhas (RNF-006) e nenhuma tela busca tudo para cortar aqui.
 */

/** 20 linhas cabem na tabela densa sem rolagem no monitor do supervisor. */
export const TAMANHO_PAGINA_PADRAO = 20;

/** Teto aceito pela API. Pedir mais só devolveria 400. */
export const TAMANHO_PAGINA_MAXIMO = 100;

export type Pagina<T> = {
  readonly itens: readonly T[];
  readonly total: number;
  readonly pagina: number;
  readonly tamanho: number;
};

/**
 * Envelope de página para qualquer item.
 *
 * Sem `total` não passa: uma barra de paginação com total inventado manda o
 * supervisor para páginas que não existem.
 */
export function paginaSchema<T extends z.ZodTypeAny>(itemSchema: T) {
  return z.object({
    itens: z.array(itemSchema),
    total: z.number().int().nonnegative(),
    pagina: z.number().int().positive(),
    tamanho: z.number().int().nonnegative(),
  });
}

export function totalDePaginas(pagina: Pagina<unknown>): number {
  if (pagina.tamanho <= 0) {
    return 1;
  }

  return Math.max(1, Math.ceil(pagina.total / pagina.tamanho));
}

const FORMATADOR = new Intl.NumberFormat('pt-BR');

/**
 * Descreve o recorte visível: "41–55 de 1.284 registros".
 *
 * O último item sai do tamanho REAL da página, não do tamanho pedido: a
 * última página quase nunca vem cheia, e "41–60 de 55" seria mentira.
 */
export function intervaloDaPagina(
  pagina: Pagina<unknown>,
  singular = 'registro',
  plural = 'registros',
): string {
  if (pagina.total === 0 || pagina.itens.length === 0) {
    return `Nenhum ${singular}`;
  }

  const primeiro = (pagina.pagina - 1) * pagina.tamanho + 1;
  const ultimo = Math.min(primeiro + pagina.itens.length - 1, pagina.total);
  const substantivo = pagina.total === 1 ? singular : plural;

  return `${FORMATADOR.format(primeiro)}–${FORMATADOR.format(ultimo)} de ${FORMATADOR.format(pagina.total)} ${substantivo}`;
}

/**
 * Quanto o recorte inteiro tem, para o canto direito da faixa de filtros.
 *
 * É o total do FILTRO, não o da página. E é DIFERENTE de `intervaloDaPagina`
 * de propósito: a barra de paginação já diz o intervalo visível, e as duas
 * faixas escreviam a mesma frase duas vezes na mesma tela.
 *
 * `vazio` existe porque o português tem gênero: "Nenhum aparelho", mas
 * "Nenhuma movimentação".
 */
export function contagemDoRecorte(
  total: number,
  singular: string,
  plural: string,
  vazio = `Nenhum ${singular}`,
): string {
  if (total === 0) {
    return vazio;
  }

  return `${FORMATADOR.format(total)} ${total === 1 ? singular : plural}`;
}

export type ParametrosDePagina = {
  readonly pagina?: number;
  readonly tamanho?: number;
};

/** Normaliza o que a tela pediu antes de virar query string. */
export function parametrosDePagina(parametros: ParametrosDePagina): {
  readonly pagina: number;
  readonly tamanho: number;
} {
  const pagina = Math.max(1, Math.trunc(parametros.pagina ?? 1));
  const tamanho = Math.min(
    TAMANHO_PAGINA_MAXIMO,
    Math.max(1, Math.trunc(parametros.tamanho ?? TAMANHO_PAGINA_PADRAO)),
  );

  return { pagina, tamanho };
}
